import { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Modal, Row } from 'react-bootstrap';
import { CustomText } from 'components/CustomText';
import { useHistory, useLocation } from 'react-router-dom';
import { ReactComponent as VoucherSvg } from 'assets/svgs/vouchers.svg';
import { ReactComponent as Pay4MeSvg } from 'assets/svgs/pay4me.svg';
import { ReactComponent as DebitCardSvg } from 'assets/svgs/debitCard.svg';
import { ReactComponent as CreditCardSvg } from 'assets/svgs/creditCard.svg';
import { ReactComponent as CashSvg } from 'assets/svgs/cash-stack.svg';
import { ReactComponent as PickupSvg } from 'assets/svgs/pickup-station.svg';
import { ReactComponent as DoorSvg } from 'assets/svgs/door-delivery.svg';
import { AddDeliveryAddressModal } from './AddDeliveryAddressModal';
import { DeliveryAddressModal } from './DeliveryAddressModal';
import { useScreenSize } from 'hooks/useScreenSize';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCreateOrderMutation,
  useCreatePaymentIntentMutation,
  useGetAreaMutation,
  useGetCountryMutation,
  useGetFreightComRatesMutation,
  useGetPublicApiMutation,
  useGetTaxesMutation,
  useGetUserUsableVouchersMutation,
  useValidateCheckoutMutation,
} from 'redux/features/user/userApi';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { show } from 'helper/toast';
import {
  saveArea,
  saveCountry,
  saveToUserAuth,
  saveUser,
} from 'redux/features/auth/authSlice';
import { convertJsonStringToObject, extractId } from 'helper/formatObject';
import { saveToUserStore } from 'redux/features/user/userSlice';
import {
  isArrayNonEmpty,
  isSubstringInArray,
  sortedDataByCreatedAt,
} from 'helper/formatArray';
import { errorMessageFormat, formatCurrency } from 'helper/formatText';
import { toMinorUnits } from 'helper/money';
import { computeCheckoutTotals } from 'pages/checkout/pricing';
import { LoadingModal } from 'components/Loader';
import { PaymentMethodModal } from './PaymentMethodModal';
import { VoucherModal } from './VoucherModal';
import { usePaystackPayment } from 'react-paystack';
import { SuccessModal } from './SuccessModal';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripeCheckoutForm from './StripeCheckoutForm';
import { DeliveryMethodModal } from './DeliveryMethodModal';
import DeliverySection from './sections/DeliverySection';
import PaymentMethodSection from './sections/PaymentMethodSection';
import VoucherSection from './sections/VoucherSection';
import OrderItemsSection from './sections/OrderItemsSection';
import OrderSummarySection from './sections/OrderSummarySection';

const CheckoutDetails = () => {
  const { isMobile, isLaptop, isMobileS } = useScreenSize();

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const {
    country: countryInit,
    area: areaInit,
    tempArea,
    tempCountry,
    user,
    countries,
    market: marketAuth,
  } = useSelector((state) => state.userAuth);
  const {
    cart: mainCart,
    tempCart,
    market,
    areas,
    userVouchers,
    minimumOrderValue = 100000,
    currentDeliveryAddress,
    currentDeliveryMethod,
    currentPaymentMethod,
    currentPayment,
    currentVoucher,
    taxes,
  } = useSelector((state) => state.userStore);

  const country =
    location?.state?.checkoutType == 'reorder' ? tempCountry : countryInit;

  const area = location?.state?.checkoutType == 'reorder' ? tempArea : areaInit;

  const cart = location?.state?.checkoutType == 'reorder' ? tempCart : mainCart;
  const [successVisible, setSuccessVisible] = useState(false);
  const [deliveryListVisible, setDeliveryListVisible] = useState(null);
  const [addDeliveryVisible, setAddDeliveryVisible] = useState(null);
  const [initialAddress, setInitialAddress] = useState(null);
  const [addDeliveryAddressVisible, setAddDeliveryAddressVisible] =
    useState(false);
  const [deliveryAddressVisible, setDeliveryAddressVisible] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [voucherDet, setVoucherDet] = useState(null);
  const [voucherVisible, setVoucherVisible] = useState(false);
  const [paymentMethodVisible, setPaymentMethodVisible] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [deliveryMethodVisible, setDeliveryMethodVisible] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [doorDeliveryService, setDoorDeliveryService] = useState({});
  const [doorDeliveryFee, setDoorDeliveryFee] = useState(0);
  const [doorDeliveryError, setDoorDeliveryError] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  // What the payment being made right now charges, recorded when it starts.
  const pendingCharge = useRef(null);
  const [freeze, setFreeze] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [addressModalOpened, setAddressModalOpened] = useState(false);
  const [newOrderDetails, setNewOrderDetails] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);

  // Every price on this page comes from computeCheckoutTotals. While a
  // payment is in flight (`freeze`), keep the totals the user confirmed.
  const liveTotals = useMemo(
    () =>
      computeCheckoutTotals({
        cartItems: cart?.cart_items,
        country,
        areaMarket: area?.market,
        taxes,
        deliveryState: deliveryAddress?.state,
        voucher: currentVoucher,
        doorDeliveryFee,
      }),
    [
      cart,
      country,
      area,
      taxes,
      deliveryAddress,
      currentVoucher,
      doorDeliveryFee,
    ]
  );
  const confirmedTotals = useRef(liveTotals);
  useEffect(() => {
    if (!freeze) confirmedTotals.current = liveTotals;
  }, [freeze, liveTotals]);
  const {
    subtotal,
    totalKg,
    deliveryFee: basicDeliveryFee,
    doorDeliveryFee: doorFee,
    serviceCharge,
    vat,
    taxRate,
    tax: countryTax,
    otherCharges,
    minimumOrderValue: mov,
    voucherBalance,
    voucherCoversAll,
    voucherDeduction,
    total: totalAmount,
  } = freeze ? confirmedTotals.current : liveTotals;

  useEffect(() => {
    setStripePromise(loadStripe(user?.STRIPE_PUBLIC_API_KEY));
  }, []);

  const [getFreightComRates, { isLoading: isLoadingGetFreightComRates }] =
    useGetFreightComRatesMutation();
  const [getArea, { isLoading: isLoadingGetArea }] = useGetAreaMutation();
  const [getCountry, { isLoading: isLoadingCountry }] = useGetCountryMutation();

  const [validateCheckout, { isLoading: isLoadingValidateCheckout }] =
    useValidateCheckoutMutation();
  const [createOrder, { isLoading: isLoadingCreateOrder }] =
    useCreateOrderMutation();

  const [createPaymentIntent, { isLoading: isLoadingPaymentIntent }] =
    useCreatePaymentIntentMutation();

  const [getUserUsableVouchers, { isLoading: isLoadingVouchers }] =
    useGetUserUsableVouchersMutation();
  const validationSchema = Yup.object().shape({
    pay4MeEmail: Yup.string().email().required().label('Email address'),
  });
  const {
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    isValid,
    errors,
    touched,
    setFieldValue,
  } = useFormik({
    initialValues: {
      pay4MeEmail: '',
    },
    validateOnMount: true,
    validateOnChange: true,
    validationSchema: validationSchema,
    onSubmit: () => {},
  });

  const [getPublicApi, { isLoading: isLoadingPublicApi }] =
    useGetPublicApiMutation();

  const fetchPublicApi = async () => {
    const res = await getPublicApi();
    if (res?.data?.status === 'success') {
      const extr = res?.data;
      dispatch(saveUser({ ...extr?.user, ...extr?.publicApi }));
    } else {
      show(dispatch, errorMessageFormat(res), {
        type: 'error',
        duration: 4000,
      });
    }
  };

  const [getTaxes, { isLoading: isLoadingGetTaxes }] = useGetTaxesMutation();

  const fetchTaxes = async () => {
    const res = await getTaxes();
    if (res?.data?.status === 'success') {
      const extr = res?.data?.data;
      dispatch(saveToUserStore({ key: 'taxes', value: extr }));
    } else {
      show(dispatch, errorMessageFormat(res), {
        type: 'error',
        duration: 4000,
      });
      history.push('/cart');
    }
  };

  useEffect(() => {
    if (
      !isArrayNonEmpty(cart?.cart_items) &&
      location.pathname === '/checkout'
    ) {
      history.push('/cart');
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchPublicApi();
    fetchTaxes();
  }, []);

  const fetchVouchers = async () => {
    const res = await getUserUsableVouchers({
      email: user?.email,
      params: { country_id: extractId(country), status: 'active' },
    });
    if (res?.data?.status === 'success') {
      const extr = res?.data?.data?.map((x) => {
        return convertJsonStringToObject({ ...x });
      });
      dispatch(
        saveToUserStore({
          key: 'userVouchers',
          value: sortedDataByCreatedAt(extr, 'desc'),
        })
      );
    } else {
      show(dispatch, errorMessageFormat(res), {
        type: 'error',
        duration: 4000,
      });
    }
  };

  useEffect(() => {
    if (!freeze) {
      setFirstLoad(true);
      dispatch(saveToUserStore({ key: 'currentDeliveryMethod', value: null }));
      dispatch(saveToUserStore({ key: 'currentDeliveryAddress', value: null }));
      dispatch(saveToUserStore({ key: 'currentPaymentMethod', value: null }));
      dispatch(saveToUserStore({ key: 'currentPayment', value: null }));
      dispatch(saveToUserStore({ key: 'currentVoucher', value: null }));
      setFirstLoad(false);
    }
  }, []);

  useEffect(() => {
    if (!freeze) {
      setDeliveryAddress(currentDeliveryAddress || '');
    }
  }, [currentDeliveryAddress]);

  useEffect(() => {
    if (
      deliveryListVisible !== null &&
      isSubstringInArray(currentDeliveryMethod?.name || '#', ['Door']) &&
      !currentDeliveryAddress
    ) {
      setDeliveryMethodVisible(false);
      setDeliveryAddressVisible(true);
    }
  }, [deliveryListVisible, currentDeliveryMethod]);

  useEffect(() => {
    if (addDeliveryVisible !== null) {
      setAddDeliveryAddressVisible(true);
    }
  }, [addDeliveryVisible]);

  useEffect(() => {
    if (!addDeliveryAddressVisible && addressModalOpened) {
      setDeliveryAddressVisible(true);
    }
  }, [addDeliveryAddressVisible]);

  const fetchArea = async () => {
    const res = await getArea(extractId(area));
    if (res?.data?.status === 'success') {
      location?.state?.checkoutType == 'reorder'
        ? dispatch(saveToUserStore({ key: 'tempArea', value: res.data?.data }))
        : dispatch(saveArea(res.data?.data));
      dispatch(
        saveToUserStore({
          key: 'market',
          value: res.data?.data?.market,
        })
      );
      location?.state?.checkoutType == 'reorder'
        ? dispatch(
            saveToUserStore({
              key: 'tempCountry',
              value: res.data?.data?.country,
            })
          )
        : dispatch(saveCountry(res.data?.data?.country));
    } else {
      show(dispatch, errorMessageFormat(res), {
        type: 'error',
        duration: 4000,
      });
      history.push('/cart');
    }
  };

  const fetchCountry = async () => {
    const res = await getCountry(extractId(country));
    if (res?.data?.status === 'success') {
      location?.state?.checkoutType == 'reorder'
        ? dispatch(
            saveToUserStore({
              key: 'tempCountry',
              value: res.data?.data,
            })
          )
        : dispatch(saveCountry(res.data?.data));
    } else {
      show(dispatch, errorMessageFormat(res), {
        type: 'error',
        duration: 4000,
      });
      history.push('/cart');
    }
  };

  const clearMarket = async () => {
    await dispatch(
      saveToUserStore({
        key: 'market',
        value: null,
      })
    );
  };

  useEffect(() => {
    if (!freeze) {
      // fetchCart();
      if (extractId(area)) {
        fetchArea();
      } else {
        clearMarket();
        fetchCountry();
      }
      fetchVouchers();
    }
  }, [dispatch, refetch, extractId(area)]);

  useEffect(() => {
    if (
      !isSubstringInArray(country?.name, ['nigeria']) &&
      location?.state?.checkoutType != 'reorder'
    ) {
      dispatch(saveToUserAuth({ key: 'area', value: null }));
    }
  }, [area, country]);

  const fetchCanadaPostRates = async () => {
    setDoorDeliveryError(null);
    const resolveValues = {
      origin_postal_code: market.pickup_station_postal_code,
      origin_name: market.pickup_station_name,
      origin_address_line_1: market.pickup_station_street_address || '',
      origin_address_line_2: '',
      origin_unit_number: market.pickup_station_apartment || '',
      origin_city: market.pickup_station_city || '',
      origin_region: market.pickup_station_state_code,
      origin_country: market.pickup_station_country_code,
      origin_residential: false,
      origin_tailgate_required:
        market.pickup_station_tailgate_required || false,
      origin_instructions: market.pickup_station_additional_info || '',
      origin_contact_name: market.pickup_station_contact_name || '',
      origin_phone_number:
        market.pickup_station_contact_phone_prefix +
          market.pickup_station_contact_phone || market.contact_phone,

      destination_name:
        currentDeliveryAddress.tag || currentDeliveryAddress.contact_name || '',
      destination_postal_code: currentDeliveryAddress.postal_code,
      destination_address_line_1: currentDeliveryAddress.street_address,
      destination_address_line_2: '',
      destination_unit_number: currentDeliveryAddress.apartment || '',
      destination_city: currentDeliveryAddress.city,
      destination_region: currentDeliveryAddress.state,
      destination_country: currentDeliveryAddress.country,
      destination_residential: true,
      destination_tailgate_required:
        currentDeliveryAddress.tailgate_required || false,
      destination_instructions: currentDeliveryAddress.additional_info || '',
      destination_contact_name: currentDeliveryAddress.contact_name,
      destination_phone_number: currentDeliveryAddress.contact_phone,

      packaging_type: 'package',
      includes_return_label: false,
      has_dangerous_goods: false,
      weight: {
        unit: 'kg',
        value: Number(totalKg.toFixed(3)),
      },
      cuboid: {
        unit: 'in',
        l: 15,
        w: 15,
        h: 15,
      },
      description: 'Groceries',
      special_handling_required: true,
    };
    const res = await getFreightComRates(resolveValues);
    if (res?.data?.status === 'success' && res?.data?.data?.lowest_rate) {
      setDoorDeliveryFee(res?.data?.data?.lowest_rate);
      setDoorDeliveryService(res?.data?.data?.lowest_service);
    } else {
      show(
        dispatch,
        res?.error?.data?.message ||
          (typeof res?.error?.error == 'string' &&
            res?.error?.error?.replace('TypeError: ', '')) ||
          'Failed to get delivery service, re-select delivery address or change delivery method',
        {
          type: 'error',
          duration: 6000,
        }
      );
      setDoorDeliveryError(
        'Failed to get delivery service, re-select delivery address or change delivery method'
      );
    }
    return;
  };

  useEffect(() => {
    if (!freeze && !firstLoad) {
      if (
        isSubstringInArray(currentDeliveryMethod?.name || '#', ['Door']) &&
        isSubstringInArray(country?.name, ['canada']) &&
        !!currentDeliveryAddress
      ) {
        setDoorDeliveryFee(0);
        setDoorDeliveryService({});
        fetchCanadaPostRates(doorDeliveryFee);
      }
      if (isSubstringInArray(currentDeliveryMethod?.name || '#', ['pickup'])) {
        setDoorDeliveryFee(0);
        setDoorDeliveryService({});
      }
    }
  }, [currentDeliveryAddress, currentDeliveryMethod]);

  const deliverMethod = useMemo(() => {
    let filtered = null;
    if (currentDeliveryMethod) {
      filtered = {
        ...currentDeliveryMethod,
        name: currentDeliveryMethod?.name,
        svg: isSubstringInArray(currentDeliveryMethod?.name, ['pickup']) ? (
          <PickupSvg />
        ) : (
          <DoorSvg />
        ),
      };
    }

    return filtered;
  }, [currentDeliveryMethod]);

  const payMethod = useMemo(() => {
    let filtered = null;
    if (currentPaymentMethod) {
      filtered = {
        ...currentPaymentMethod,
        name: currentPaymentMethod?.name,
        svg: isSubstringInArray(currentPaymentMethod?.name, [
          'paystack',
          'stripe',
        ]) ? (
          <DebitCardSvg />
        ) : isSubstringInArray(currentPaymentMethod?.name, ['voucher']) ? (
          <VoucherSvg />
        ) : isSubstringInArray(currentPaymentMethod?.name, ['cash']) ? (
          <CashSvg />
        ) : isSubstringInArray(currentPaymentMethod?.name, ['pay4me']) ? (
          <Pay4MeSvg />
        ) : (
          <CreditCardSvg />
        ),
      };
    }

    return filtered;
  }, [currentPaymentMethod]);

  const voucherDetails = useMemo(() => {
    let filtered = null;
    if (currentVoucher) {
      filtered = {
        ...currentVoucher,
        name: currentVoucher?.name?.toUpperCase(),
        svg: <VoucherSvg />,
      };
    }

    return filtered;
  }, [currentVoucher]);

  // A voucher that covers the whole order replaces the payment method, also
  // when the order shrinks under an already-applied voucher (door -> pickup).
  useEffect(() => {
    if (currentVoucher && voucherCoversAll) {
      dispatch(saveToUserStore({ key: 'currentPaymentMethod', value: null }));
    }
  }, [currentVoucher, voucherCoversAll]);

  const filteredData = useMemo(() => {
    return (cart?.cart_items || []).map((x) => {
      return {
        name: x?.product_id?.name,
        quantity: x?.quantity,
        price: x?.priceByMarket_id?.price,
        uom: x?.priceByMarket_id?.uom_id?.name,
        image: x?.product_id?.imageCover,
        kg: x?.priceByMarket_id?.kg,
        market: x?.priceByMarket_id?.market_id,
        total_kg: x?.priceByMarket_id?.kg * x?.quantity,
        id: x?.product_id?.id,
        cart_item_id: x?.id,
        cart_id: cart?.id,
      };
    });
  }, [cart]);

  // Keep the stored market in step with the cart; it is the freight-rate
  // origin and is sent with the order.
  useEffect(() => {
    if (freeze) return;
    const firstItemMarket = cart?.cart_items?.[0]?.priceByMarket_id?.market_id;
    dispatch(
      saveToUserAuth({ key: 'market', value: area?.market || firstItemMarket })
    );
    if (country?.name?.toLowerCase() !== 'nigeria') {
      dispatch(saveToUserStore({ key: 'market', value: firstItemMarket }));
    }
  }, [cart, market, area, country]);

  const currencySymbol = country?.name?.toLowerCase()?.includes('nigeria')
    ? '₦'
    : country?.currency?.symbol;

  // const paystackWebViewRef = useRef(paystackProps.PayStackRef);

  const config = {
    email: user?.email,
    phone: user?.phone,
    firstname: user?.firstName,
    lastname: user?.lastName,
    amount: toMinorUnits(totalAmount),
    currency: 'NGN',
    publicKey: user?.PAYSTACK_PUBLIC_API_KEY,
  };

  const onPayStackClose = (e) => {
    show(dispatch, 'Payment was cancelled', {
      type: 'error',
      placement: 'top',
      duration: 4000,
    });
  };
  const onPayStackSuccess = (res) => {
    handlePaystackPayment(res?.reference || res?.trxref);
  };

  const initializePayStackPayment = usePaystackPayment(config);

  const handlePayment = async () => {
    // A payment went through but its order failed. Retry with it only while
    // the order matches what was charged; otherwise the order would be paid
    // at an amount nobody charged, or placed unpaid next to the charge.
    if (currentPayment) {
      if (paymentMatchesOrder()) {
        const validated = await handleValidation();
        if (validated) handleCheckout({ reference: currentPayment.ref });
      } else {
        show(
          dispatch,
          `This order no longer matches the ${paidAmount} you already paid. ` +
            'Change it back to the same total and payment method to finish, ' +
            'or contact support from the Help page for a refund.',
          { type: 'error', duration: 6000 }
        );
      }
      return;
    }
    if (isSubstringInArray(currentPaymentMethod?.name, ['paystack'])) {
      const validated = await handleValidation();
      if (validated) {
        pendingCharge.current = { provider: 'paystack', amount: config.amount };
        initializePayStackPayment(onPayStackSuccess, onPayStackClose);
      } else {
        show(
          dispatch,
          errorMessageFormat(
            'Something went wrong, Go back to cart and try again'
          ),
          {
            type: 'error',
          }
        );
      }
    } else if (isSubstringInArray(currentPaymentMethod?.name, ['stripe'])) {
      const validated = await handleValidation();
      if (validated) {
        setStripeLoading(true);
        const amount = toMinorUnits(totalAmount);
        const response = await createPaymentIntent({
          amount,
          currency: country?.currency?.code,
        });
        if (response.error || !response.data.data) {
          show(
            dispatch,
            response?.error?.data?.message || errorMessageFormat(response),
            {
              type: 'error',
            }
          );
          setStripeLoading(false);
          return;
        }
        setStripeLoading(false);
        pendingCharge.current = { provider: 'stripe', amount };
        setStripeClientSecret(response.data.data);

        // const initRes = await initPaymentSheet({
        //   merchantDisplayName: 'Fooddy NG.',
        //   paymentIntentClientSecret: response.data.data,
        //   defaultBillingDetails: {
        //     name: user?.firstName + ' ' + user?.lastName,
        //   },
        // });
        // const { error: paymentSheetError } = initRes;

        // if (paymentSheetError) {
        //   show(dispatch, errorMessageFormat(paymentSheetError.message), {
        //     type: 'error',
        //   });
        //   setStripeLoading(false);
        //   return;
        // }
        // // const { error: paymentError } = await presentPaymentSheet();
        // const ress = await presentPaymentSheet();
        // if (ress?.error) {
        //   // show( dispatch,
        //   //   `Error code: ${ress?.error.code}\n${ress.error.message}` ||
        //   //     errorMessageFormat(res),
        //   //   {
        //   //     type: 'error',
        //   //   }
        //   // );

        //   show(
        //     dispatch,
        //     `${ress.error.message}` || errorMessageFormat(ress.error.message),
        //     {
        //       type: 'error',
        //     }
        //   );
        //   setStripeLoading(false);
        //   return;
        // }
        // // console.log('ress', ress);

        // await handleStripePayment(response.data.data);
      } else {
        show(
          dispatch,
          errorMessageFormat('Go back to your cart and try again'),
          {
            type: 'error',
          }
        );
      }
    } else if (
      isSubstringInArray(currentPaymentMethod?.name, ['cash']) ||
      isSubstringInArray(currentPaymentMethod?.name, ['pay4me']) ||
      (!currentPaymentMethod && !!currentVoucher)
    ) {
      handleCheckout({ reference: null });
    } else {
      show(dispatch, 'Complete all neccessary options for checkout', {
        type: 'error',
      });
    }
  };

  // Remember a completed payment with its provider and the amount charged, so
  // a retry after a failed order reuses it instead of charging again.
  const rememberPayment = (ref) => {
    dispatch(
      saveToUserStore({
        key: 'currentPayment',
        value: { ...pendingCharge.current, ref },
      })
    );
  };

  const paymentMatchesOrder = () =>
    !!currentPaymentMethod?.name &&
    isSubstringInArray(currentPaymentMethod.name, [currentPayment.provider]) &&
    toMinorUnits(totalAmount) === currentPayment.amount;

  const paidAmount = currentPayment
    ? formatCurrency(currentPayment.amount / 100, 2, currencySymbol)
    : null;

  const handleStripePayment = async (ref) => {
    rememberPayment(ref);
    setStripeClientSecret(null); // paid: close the card form
    await handleCheckout({ reference: ref });
  };

  const handlePaystackPayment = async (ref) => {
    rememberPayment(ref);
    await handleCheckout({ reference: ref });
  };

  const handleValidation = async () => {
    setFreeze(true);
    const res = await validateCheckout({
      payment_method_id: extractId(currentPaymentMethod),
      cart_id: extractId(cart),
      delivery_address_id: extractId(currentDeliveryAddress),
      delivery_method: isSubstringInArray(currentDeliveryMethod?.name || '#', [
        'pickup',
      ])
        ? 'pickup'
        : 'door',
      voucher_id: extractId(currentVoucher),
      area_id: extractId(area),
      market_id: extractId(market) || extractId(marketAuth),
      country_id: extractId(country),
      calculated_tax: taxRate,
      calculated_amount: totalAmount,
      freight_service: doorDeliveryService?.service_id,
      checkoutType: location?.state?.checkoutType || null,
    });
    if (res?.data?.status === 'success') {
      return true;
    } else {
      show(
        dispatch,
        res?.error?.data?.message ||
          (typeof res?.error?.error == 'string' &&
            res?.error?.error?.replace('TypeError: ', '')),
        {
          type: 'error',
        }
      );
      setFreeze(false);
      return false;
    }
  };

  const handleCheckout = async ({ reference = null }) => {
    setFreeze(true);
    const res = await createOrder({
      payment_method_id: extractId(currentPaymentMethod),
      cart_id: extractId(cart),
      delivery_address_id: extractId(currentDeliveryAddress),
      delivery_method: isSubstringInArray(currentDeliveryMethod?.name || '#', [
        'pickup',
      ])
        ? 'pickup'
        : 'door',
      voucher_id: extractId(currentVoucher),
      area_id: extractId(area),
      market_id: extractId(market) || extractId(marketAuth),
      country_id: extractId(country),
      payment_status: reference ? 'paid' : 'unpaid',
      payment_reference: reference,
      calculated_tax: taxRate,
      calculated_amount: totalAmount,
      pay4MeEmail: values?.pay4MeEmail,
      freight_service: doorDeliveryService?.service_id,
      checkoutType: location?.state?.checkoutType || null,
    });
    setStripeLoading(false);
    if (res?.data?.status === 'success') {
      // show( dispatch,res?.data?.message, {
      //   type: 'success',
      // });
      // navigation.navigate('Categories');
      setNewOrderDetails(res?.data?.data?.order || res?.data?.data);
      dispatch(saveToUserStore({ key: 'currentPayment', value: null }));
      dispatch(
        saveToUserStore({
          key: location?.state?.checkoutType == 'reorder' ? 'tempCart' : 'cart',
          value: null,
        })
      );
      location?.state?.checkoutType == 'reorder' &&
        dispatch(
          saveToUserStore({
            key: 'cartItemCount',
            value: 0,
          })
        );
      setSuccessVisible(true);
      setFreeze(false);
    } else {
      show(
        dispatch,
        res?.error?.data?.message ||
          (typeof res?.error?.error == 'string' &&
            res?.error?.error?.replace('TypeError: ', '')),
        {
          type: 'error',
        }
      );
      setFreeze(true);
    }
  };

  const options = useMemo(() => {
    return [
      {
        label: 'Item total',
        value: formatCurrency(subtotal, 2, currencySymbol),
      },
      {
        label: `Delivery${
          totalKg &&
          !isSubstringInArray(country?.name, ['nigeria']) &&
          !isSubstringInArray(currentDeliveryMethod?.name || '#', ['Door'])
            ? '(' +
              totalKg.toFixed(3) +
              'kg at ' +
              currencySymbol +
              country?.delivery_fee_by_kg +
              '/kg)'
            : ''
        }`,
        // doorFee is the numeric freight fee from the totals; the raw state
        // can be a string ("25.50") and would concatenate.
        value: formatCurrency(basicDeliveryFee + doorFee, 2, currencySymbol),
      },
      {
        label: 'Service charge',
        value: formatCurrency(serviceCharge, 2, currencySymbol),
        show: !!serviceCharge,
      },
      {
        label: 'VAT',
        value: formatCurrency(vat, 2, currencySymbol),
        show: !!country?.vat,
      },
      {
        label: `GST/PST (${Number(taxRate).toFixed(3)}%)`.replace('.000', ''),
        value: formatCurrency(countryTax, 2, currencySymbol),
        show: !!countryTax,
      },
      {
        label: `Others`,
        value: formatCurrency(otherCharges, 2, currencySymbol),
        show: !!country?.other_charges,
      },
      {
        label: `Voucher Deduction`,
        value: `- ${formatCurrency(voucherDeduction, 2, currencySymbol)}`,
        show: !!voucherDetails,
      },
    ]?.filter((x) => x?.show || x?.show === undefined);
  }, [
    subtotal,
    currencySymbol,
    basicDeliveryFee,
    doorFee,
    serviceCharge,
    vat,
    taxRate,
    countryTax,
    otherCharges,
    totalKg,
    country,
    voucherDeduction,
    voucherDetails,
    currentDeliveryMethod,
  ]);

  return (
    <Container>
      <LoadingModal
        show={
          isLoadingValidateCheckout ||
          isLoadingCreateOrder ||
          stripeLoading ||
          isLoadingCountry ||
          isLoadingGetArea ||
          isLoadingGetTaxes
        }
        text={
          isLoadingValidateCheckout
            ? 'Validating checkout'
            : isLoadingCreateOrder
            ? `Placing order\nPlease do not leave or close the tab until this process is done`
            : 'Loading...'
        }
      />
      <Row className="mb-3 mt-3 mt-lg-0 mt-md-0">
        <Col lg="auto" md={12} className="d-flex align-items-center py-2">
          <CustomText
            fontFamily={'Poppins'}
            fontSize={16}
            fontWeight={600}
            text={'Checkout'}
            // cNColor="gray"
          />
        </Col>
      </Row>
      <Row>
        <DeliverySection
          country={country}
          currentDeliveryAddress={currentDeliveryAddress}
          setFreeze={setFreeze}
          setDeliveryAddressVisible={setDeliveryAddressVisible}
          deliverMethod={deliverMethod}
          setDeliveryMethodVisible={setDeliveryMethodVisible}
          deliveryMethod={deliveryMethod}
          doorDeliveryFee={doorDeliveryFee}
          isLoadingGetFreightComRates={isLoadingGetFreightComRates}
          currencySymbol={currencySymbol}
          doorDeliveryService={doorDeliveryService}
          currentDeliveryMethod={currentDeliveryMethod}
          doorDeliveryError={doorDeliveryError}
        />
        <PaymentMethodSection
          payMethod={payMethod}
          setFreeze={setFreeze}
          setPaymentMethodVisible={setPaymentMethodVisible}
          voucherDetails={voucherDetails}
          voucherCoversAll={voucherCoversAll}
          paidAmount={paidAmount}
          values={values}
          errors={errors}
          handleChange={handleChange}
        />
        <VoucherSection
          voucherDetails={voucherDetails}
          voucherBalance={voucherBalance}
          setFreeze={setFreeze}
          setVoucherDet={setVoucherDet}
          dispatch={dispatch}
          setVoucherVisible={setVoucherVisible}
          currencySymbol={currencySymbol}
        />
      </Row>
      <Row>
        <OrderItemsSection
          filteredData={filteredData}
          isMobile={isMobile}
          currencySymbol={currencySymbol}
        />
        <OrderSummarySection
          options={options}
          isMobile={isMobile}
          totalAmount={totalAmount}
          currencySymbol={currencySymbol}
          handlePayment={handlePayment}
          subtotal={subtotal}
          mov={mov}
          currentDeliveryAddress={currentDeliveryAddress}
          currentPaymentMethod={currentPaymentMethod}
          errors={errors}
          voucherDetails={voucherDetails}
          voucherCoversAll={voucherCoversAll}
          currentDeliveryMethod={currentDeliveryMethod}
          country={country}
          doorDeliveryFee={doorDeliveryFee}
        />
      </Row>
      <PaymentMethodModal
        visible={paymentMethodVisible}
        setVisible={(val) => setPaymentMethodVisible(val)}
        selectedMethod={paymentMethod}
        setSelectedMethod={(val) => setPaymentMethod(val)}
        excludeMethods={['voucher']}
        tempCountry={country}
      />
      <DeliveryAddressModal
        visible={deliveryAddressVisible}
        setVisible={(val) => {
          setDeliveryAddressVisible(val);
        }}
        selectedAddress={deliveryAddress}
        setSelectedAddress={(addr) => {
          setDeliveryAddress(addr);
        }}
        setInitialAddress={(val) => {
          setInitialAddress(val);
        }}
        addDeliveryVisible={addDeliveryVisible}
        setAddDeliveryVisible={(val) => {
          setAddDeliveryVisible(val);
        }}
        addressModalOpened={addressModalOpened}
        setAddressModalOpened={(val) => {
          setAddressModalOpened(val);
        }}
      />
      <DeliveryMethodModal
        visible={deliveryMethodVisible}
        setVisible={(val) => setDeliveryMethodVisible(val)}
        selectedMethod={deliveryMethod}
        setSelectedMethod={(val) => setDeliveryMethod(val)}
        deliveryListVisible={deliveryListVisible}
        setDeliveryListVisible={(val) => setDeliveryListVisible(val)}
      />
      <AddDeliveryAddressModal
        visible={addDeliveryAddressVisible}
        setVisible={(val) => setAddDeliveryAddressVisible(val)}
        initial={initialAddress}
      />
      <VoucherModal
        visible={voucherVisible}
        setVisible={setVoucherVisible}
        selectedMethod={voucherDet}
        setSelectedMethod={setVoucherDet}
        tempCountry={country}
      />
      <SuccessModal
        visible={successVisible}
        setVisible={setSuccessVisible}
        details={newOrderDetails}
        text={`Your Order, ID ${
          newOrderDetails?.order?.order_id || newOrderDetails?.order_id
        } was placed successfully.\nCheck order details to track this order`}
      />
      <Modal
        centered
        backdrop="static"
        onHide={() => setStripeClientSecret(null)}
        show={
          stripeClientSecret &&
          stripePromise &&
          !stripeLoading &&
          !isLoadingCreateOrder
        }
      >
        <Modal.Header closeButton>
          <CustomText
            fontFamily={'Poppins'}
            fontSize={16}
            fontWeight={600}
            text="Payment"
          />
        </Modal.Header>
        <Modal.Body>
          {stripeClientSecret && stripePromise && !stripeLoading && (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: stripeClientSecret,
                appearance: { theme: 'stripe' },
              }}
            >
              <StripeCheckoutForm
                handleSuccess={() => {
                  handleStripePayment(stripeClientSecret);
                }}
                loading={isLoadingCreateOrder}
              />
            </Elements>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default CheckoutDetails;
